const state = () => ({
  allIds: [],
  byId: {}
});

const getters = {
  find: (state, _, rootState) => id => {
    if (!state.byId?.[id]) return;

    return {
      ...state.byId[id],
      deal: rootState.deals.byId[state.byId[id].deal_id]
    };
  },

  getOrderDealsByDeal: (_, getters) => deal =>
    getters.list.filter(od => od.deal_id === deal.id),

  getRedemptions: (_, getters) => dealId => {
    const orderDeals = getters.list.filter(
      ({ deal_id }) => deal_id === dealId
    );

    const od = orderDeals.find(
      ({ deal: { max_product_redemptions: mpr } }) => !!mpr
    );

    if (!od?.deal?.max_product_redemptions) return null;

    const {
      deal: { totalProductsCount }
    } = od;

    const maxProductRedemptions = orderDeals.reduce(
      (
        sum,
        { deal: { max_product_redemptions: mpr }, added_max_redemptions: adr }
      ) => sum + mpr + (adr ?? 0),
      0
    );

    if (totalProductsCount <= maxProductRedemptions) return null;

    const remaining = orderDeals.reduce(
      (sum, { redemptions_remaining }) => sum + redemptions_remaining,
      0
    );

    return { remaining, maxProductRedemptions };
  },

  // One entry per purchased package that limits how many DIFFERENT deals the holder may start
  // (packages.max_deal_redemptions). This allowance belongs to the whole pass, not a single deal,
  // so it is surfaced on the experiences page that lists every deal - never on an individual deal
  // page. Empty when no purchased package sets a limit.
  packageDealsSummary: (_, getters) => {
    const byPackage = new Map();

    getters.list.forEach(od => {
      if (!(od.package_deals_limit > 0)) return;

      const existing = byPackage.get(od.order_package_id);

      // Every deal of one package shares the same remaining; keep the highest to stay truthful
      // against any transient drift between deals of the same package.
      if (!existing || od.package_deals_remaining > existing.remaining) {
        byPackage.set(od.order_package_id, {
          orderPackageId: od.order_package_id,
          remaining: od.package_deals_remaining,
          limit: od.package_deals_limit
        });
      }
    });

    return [...byPackage.values()];
  },

  hasRedemptionsRemaining: (state, getters) => id => {
    // The pass has used up its allowance of different deals and this one is not among them.
    // Mirrors the gate in Product::canRedeemProduct so the ticket reads as unavailable instead
    // of being refused at the venue. Deals already holding a slot stay usable.
    if (getters.isBlockedByPackageCap(id)) return false;

    return (
      state.byId[id]?.redemptions_remaining === null ||
      state.byId[id]?.redemptions_remaining > 0
    );
  },

  isBlockedByPackageCap: state => id => {
    const od = state.byId[id];

    if (!od?.package_deals_limit || od.package_slot_used) return false;

    return od.package_deals_remaining <= 0;
  },

  // Same question as isBlockedByPackageCap but asked with a deal id, for the deal page - which only
  // knows the deal from the route. A deal the holder owns through several passes is only out of
  // reach when the cap blocks it on every one of them.
  isDealBlockedByPackageCap: (_, getters) => dealId => {
    const orderDeals = getters.list.filter(od => od.deal_id === dealId);

    return (
      orderDeals.length > 0 &&
      orderDeals.every(od => getters.isBlockedByPackageCap(od.id))
    );
  },

  list: (state, getters) => state.allIds.map(id => getters.find(id))
};

const actions = {
  addOrderDeals({ commit, dispatch }, orderDeals) {
    orderDeals.forEach(od => {
      od.deal = [od.deal_id];
      od.type = 'deal';
      commit('add', od);
    });
    dispatch('progressTracker/initializeProgressTracker', null, { root: true });
  },

  removeOrderDeals({ commit }, orderDealIds) {
    commit('removeAll', orderDealIds);
  },

  updateOrderDealRedemptions(
    { commit },
    { orderDealId, redemptionsRemainingUse = 1 }
  ) {
    if (!orderDealId) return;

    commit('incrementRedemptionsMade', orderDealId);
    if (redemptionsRemainingUse) commit('decrementRedemptionsRemaining', orderDealId);
    commit('consumePackageDealSlot', orderDealId);
  }
};

const mutations = {
  add(state, orderDeal) {
    state.byId[orderDeal.id] = orderDeal;

    if (!state.allIds.includes(orderDeal.id)) {
      state.allIds.push(orderDeal.id);
    }
  },

  removeAll(state, orderDealIds) {
    orderDealIds.forEach(id => {
      delete state.byId[id];
    });

    state.allIds = state.allIds.filter(
      id => !orderDealIds.includes(id)
    );
  },

  incrementRedemptionsMade(state, orderDealId) {
    const orderDeal = state.byId[orderDealId];
    if (!orderDeal) return;

    if ('redemptions_made' in orderDeal) {
      orderDeal.redemptions_made += 1;
    }
  },

  // The FIRST redemption of a deal takes one of the purchased package's slots, so every deal of
  // that package the holder has not started yet must stop looking available right away - the
  // server refuses them from here on. Further redemptions of the same deal take nothing.
  consumePackageDealSlot(state, orderDealId) {
    const redeemed = state.byId[orderDealId];

    if (!redeemed?.package_deals_limit || redeemed.package_slot_used) return;

    state.allIds.forEach(id => {
      const orderDeal = state.byId[id];

      if (orderDeal?.order_package_id !== redeemed.order_package_id) return;

      orderDeal.package_deals_remaining = Math.max(
        0,
        orderDeal.package_deals_remaining - 1
      );

      if (orderDeal.deal_id === redeemed.deal_id) {
        orderDeal.package_slot_used = true;
      }
    });
  },

  decrementRedemptionsRemaining(state, orderDealId) {
    const orderDeal = state.byId[orderDealId];
    if (!orderDeal) return;

    if (
      'redemptions_remaining' in orderDeal &&
      orderDeal.redemptions_remaining > 0
    ) {
      orderDeal.redemptions_remaining -= 1;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
